import { parseDescription, isMythicItem } from "../item-parsers";
import { GoldLogo } from "./ItemCard";
import { ItemLink } from "../ItemLink";
import { HeaderBullet } from "@ugg/shared/components/common/HeaderBullet";
import { TooltipContainer, CostAnalysisTooltip } from "@outplayed/tooltips";
import { costEfficiencyMap } from "./cost-efficiency-map";
import type { Item } from "@outplayed/riot-assets";

// some custom mythic passives since they are structured diff from the rest
const mythicPassiveEff: Record<number, { cost: number; stats: string[] }> = {
  3001: {
    cost: costEfficiencyMap.Armor * 5 + costEfficiencyMap["Magic Resist"] * 5,
    stats: ["5 Armor", "5 Magic Resist"],
  },
  7023: {
    cost: costEfficiencyMap.Armor * 5 + costEfficiencyMap["Magic Resist"] * 5,
    stats: ["5 Armor", "5 Magic Resist"],
  },
  3190: {
    cost: costEfficiencyMap.Armor * 2 + costEfficiencyMap["Magic Resist"] * 2,
    stats: ["2 Armor", "2 Magic Resist"],
  },
  7019: {
    cost: costEfficiencyMap.Armor * 2 + costEfficiencyMap["Magic Resist"] * 2,
    stats: ["2 Armor", "2 Magic Resist"],
  },
};

interface ItemCostAnalysisProps {
  itemId: number;
  itemData: Item;
}

export function ItemCostAnalysis(props: ItemCostAnalysisProps) {
  const { itemData, itemId } = props;

  if (!itemData || !itemId) return null;

  const { allStats, mythicPassive } = parseDescription(itemData.description);
  const isMythic = isMythicItem(itemData.description);

  const regex =
    /[0-9]\.?\d+%|[0-9]+|Attack Damage|Ability Power|Health|Magic Resist|Attack Speed|Move Speed|Ability Haste|Armor Penetration|Lethality|Life Steal|Omnivamp|Critical Strike Chance|Magic Penetration|Gold Per 10 Seconds|Base Health Regen|Base Mana Regen|Heal and Shield Power|Mana|Armor/g;

  const calcTotalValue = (stats: string[]) => {
    let total = 0;
    stats.forEach((stat) => {
      const { cost } = calcStatCost(stat) || {};
      total += cost ?? 0;
    });
    return total;
  };

  const calcStatCost = (stat: string) => {
    const split = stat.match(regex);

    if (split?.length === 2 && costEfficiencyMap[split[1]]) {
      let cost = 0;
      const statAmount = split[0];
      const statName = split[1];
      const isPercent = statAmount.includes("%");
      const percent = parseFloat(split[0].replace("%", ""));
      const statValue = parseFloat(split[0]);

      if (split[1] === "Magic Penetration" && isPercent) {
        cost = costEfficiencyMap["%Magic Penetration"] * percent;
      } else if (split[1] === "Armor Penetration" && isPercent) {
        cost = costEfficiencyMap["%Armor Penetration"] * percent;
      } else if (split[1] === "Move Speed" && isPercent) {
        // add percent movespeed
        cost = costEfficiencyMap["%Move Speed"] * percent;
      } else {
        cost = costEfficiencyMap[statName] * (isPercent ? percent : statValue);
      }
      // console.log(cost, statAmount, statName);
      return {
        cost,
        statAmount, // want to keep %
        statName,
      };
    }
    return null;
  };

  const calcMythicPassiveCost = (itemId: number, passive: string) => {
    let total = 0;
    const stats: Array<{ cost: number; statAmount: string; statName: string }> = [];

    if (mythicPassiveEff[itemId]) {
      mythicPassiveEff[itemId].stats?.forEach((stat) => {
        const statCost = calcStatCost(stat);
        if (statCost) {
          const { cost, statAmount, statName } = statCost;
          total += cost;
          stats.push({ cost, statAmount, statName });
        }
        // console.log(cost, statAmount, statName);
      });
    } else {
      // we can grab the stats from the passive description
      const mythicRegex =
        /[0-9]+% (Attack Damage|Ability Power|Health|Magic Resist|Attack Speed|Move Speed|Ability Haste|Armor Penetration|Lethality|Life Steal|Omnivamp|Critical Strike Chance|Magic Penetration|Gold Per 10 Seconds|Base Health Regen|Base Mana Regen|Heal and Shield Power|Mana|Armor)|[0-9]+ (Attack Damage|Ability Power|Health|Magic Resist|Attack Speed|Move Speed|Ability Haste|Armor Penetration|Lethality|Life Steal|Omnivamp|Critical Strike Chance|Magic Penetration|Gold Per 10 Seconds|Base Health Regen|Base Mana Regen|Heal and Shield Power|Mana|Armor)/g;
      const mythicStats = passive?.match(mythicRegex);

      mythicStats?.forEach((stat) => {
        const statCost = calcStatCost(stat);
        if (statCost) {
          const { cost, statAmount, statName } = statCost;
          total += cost;
          stats.push({ cost, statAmount, statName });
        }
      });
    }
    return { total, stats };
  };

  const { total: mythicTotal, stats: mythicStats } = calcMythicPassiveCost(itemId, mythicPassive);

  const totalValue = Math.round(calcTotalValue(allStats));

  const calcGoldEff = (totalValue: number, totalCost: number) => {
    if (totalValue && totalCost) {
      return totalValue / totalCost;
    }
    return 0;
  };

  const goldEff = calcGoldEff(totalValue, itemData.gold?.total);

  const itemPicProps = {
    itemId,
    className: isMythic ? "items-pic-xsmall items-border-mythic-small" : "items-pic-xsmall items-border-normal-small",
  };

  const tooltipInfo = {
    title: "Cost Analysis",
    info1:
      "Stat gold values are derived from the cheapest item component that provides that specific stat. (e.g., A longsword that costs 350g and provides 10 AD tells us 1 AD is worth 35g)",
    info2:
      "Gold efficiency is the ratio between the sum of the gold values and the total cost of the item. This gives us a benchmark of how we can evaluate the stats of an item.",
  };

  return (
    <div className="items-cost-analysis">
      <HeaderBullet className="mx-[18px] mb-[24px]">
        <TooltipContainer tooltipInfo={<CostAnalysisTooltip {...tooltipInfo} />}>
          <span>Cost Analysis</span>
        </TooltipContainer>
      </HeaderBullet>

      <div className="cost-analysis-container">
        <div className="cost-section stat-values">
          <div className="stat-values-line">
            Total gold value = <GoldLogo cost={totalValue ? totalValue : 0} />
          </div>
          {allStats?.length > 0 &&
            allStats.map((stat) => {
              const { cost, statAmount, statName } = calcStatCost(stat) || {};
              if (!cost || !statAmount || !statName) return null;
              return (
                <div className="stat-values-line-dot" key={stat}>
                  {statAmount}&nbsp;
                  <strong>{statName}</strong>
                  &nbsp;= <GoldLogo cost={Math.round(cost)} />
                </div>
              );
            })}
        </div>
        {isMythic && mythicStats?.length > 0 && (
          <div className="cost-section">
            <div className="stat-values-line">
              Total mythic passive gold value = <GoldLogo cost={Math.round(mythicTotal)} />
            </div>
            {mythicStats?.map((stat) => (
              <div key={stat.statName} className="stat-values-line-dot">
                {stat.statAmount}&nbsp;<strong>{stat.statName}</strong>&nbsp;= <GoldLogo cost={Math.round(stat.cost)} />
              </div>
            ))}
          </div>
        )}

        <div className="cost-section">
          <div className="stat-values-line">Gold efficiency</div>
          <div className="stat-values-line-dot">
            <div className="cost-analysis-pic">
              <ItemLink {...itemPicProps} />
            </div>

            <span>
              <strong className="item-name-text">{itemData.name}'s</strong>
              &nbsp;base stats are {`${Math.round(goldEff * 100)}`}% gold efficient.
            </span>
          </div>
          {isMythic && mythicTotal > 0 && (
            <div className="stat-values-line-dot">
              <div className="cost-analysis-pic">
                <ItemLink {...itemPicProps} />
              </div>
              <span>
                <strong className="item-name-text">{itemData.name}'s</strong>
                &nbsp;gold efficiency is increased by {`${Math.round((mythicTotal / itemData.gold?.total) * 100)}`}% for every
                Legendary item.
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
